import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import {
  IconButton,
  Pagination,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

const SchemesList = () => {
  const navigate = useNavigate();
  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    searchParams: "", // Changed from 'search' to 'searchParams' to match API
    district: "",
    block: "",
    gramPanchayat: "",
    sector: "",
    subSector: "",
    financialYear: "",
    schemeStatus: "",
    tenderStatus: "",
    paymentStatus: "",
    ddcCumCEOName: "",
    districtEngineerName: "",
    ddo: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const filtersRef = useRef(null);

  // Pagination states
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const [filterOptions, setFilterOptions] = useState({
    districts: [],
    blocks: [],
    gramPanchayats: [],
    sectors: [],
    subSectors: [],
    financialYears: [],
    schemeStatuses: [],
    tenderStatuses: [],
    paymentStatuses: [],
    ddcCumCEOs: [],
    districtEngineers: [],
    ddos: [],
  });

  // Solid color scheme for different filter categories
  const filterCategoryColors = {
    location: {
      bg: "bg-blue-100",
      border: "border-blue-300",
      text: "text-blue-800",
    },
    sector: {
      bg: "bg-purple-100",
      border: "border-purple-300",
      text: "text-purple-800",
    },
    status: {
      bg: "bg-green-100",
      border: "border-green-300",
      text: "text-green-800",
    },
    financial: {
      bg: "bg-orange-100",
      border: "border-orange-300",
      text: "text-orange-800",
    },
    officer: {
      bg: "bg-indigo-100",
      border: "border-indigo-300",
      text: "text-indigo-800",
    },
    search: {
      bg: "bg-gray-100",
      border: "border-gray-300",
      text: "text-gray-800",
    },
  };

  useEffect(() => {
    fetchSchemes();
  }, [filters, pagination.pageNo, pagination.pageSize]);

  // Close filters when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchSchemes = async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        pageNo: pagination.pageNo,
        pageSize: pagination.pageSize,
        searchParams: filters.searchParams, // Changed from search to searchParams
      };

      // Add other filters only if they have values
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "searchParams") {
          params[key] = value;
        }
      });

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(data?.data || []);
      setPagination((prev) => ({
        ...prev,
        totalCount: data?.count || 0,
        totalPages: Math.ceil((data?.count || 0) / pagination.pageSize),
      }));

      setSchemeStats({
        total: data?.count,
        totalDraftSchemes: data?.totalDraftSchemes,
        totalCompletedSchemes: data?.totalCompletedSchemes,
        totalAllocationAmount: data?.totalAllocationAmount,
      });

      updateFilterOptions(data?.data || []);
    } catch (error) {
      console.error("Error fetching schemes:", error);
      toast.error("Failed to fetch schemes");
    } finally {
      setLoading(false);
    }
  };

  const updateFilterOptions = (schemesData) => {
    const options = {
      districts: [
        ...new Set(schemesData.map((s) => s.district).filter(Boolean)),
      ],
      blocks: [...new Set(schemesData.map((s) => s.block).filter(Boolean))],
      gramPanchayats: [
        ...new Set(schemesData.map((s) => s.gramPanchayat).filter(Boolean)),
      ],
      sectors: [...new Set(schemesData.map((s) => s.sector).filter(Boolean))],
      subSectors: [
        ...new Set(schemesData.map((s) => s.subSector).filter(Boolean)),
      ],
      financialYears: [
        ...new Set(schemesData.map((s) => s.financialYear).filter(Boolean)),
      ],
      schemeStatuses: [
        ...new Set(schemesData.map((s) => s.schemeStatus).filter(Boolean)),
      ],
      tenderStatuses: [
        ...new Set(schemesData.map((s) => s.tenderStatus).filter(Boolean)),
      ],
      paymentStatuses: [
        ...new Set(schemesData.map((s) => s.paymentStatus).filter(Boolean)),
      ],
      ddcCumCEOs: [
        ...new Set(schemesData.map((s) => s.ddcCumCEOName).filter(Boolean)),
      ],
      districtEngineers: [
        ...new Set(
          schemesData.map((s) => s.districtEngineerName).filter(Boolean)
        ),
      ],
      ddos: [...new Set(schemesData.map((s) => s.ddo).filter(Boolean))],
    };

    setFilterOptions(options);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // Reset to page 1 when filters change
    if (key !== "searchParams") {
      setPagination((prev) => ({ ...prev, pageNo: 1 }));
    }
  };

  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, searchParams: value }));
    // Reset to page 1 when searching
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const removeFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
    if (key !== "searchParams") {
      setPagination((prev) => ({ ...prev, pageNo: 1 }));
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const clearFilters = () => {
    setFilters({
      searchParams: "",
      district: "",
      block: "",
      gramPanchayat: "",
      sector: "",
      subSector: "",
      financialYear: "",
      schemeStatus: "",
      tenderStatus: "",
      paymentStatus: "",
      ddcCumCEOName: "",
      districtEngineerName: "",
      ddo: "",
    });
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, pageNo: value }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: event.target.value,
      pageNo: 1, // Reset to first page when page size changes
    }));
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(
      (value) => value !== "" && value !== null && value !== undefined
    ).length;
  };

  const getFilterCategory = (key) => {
    const categoryMap = {
      district: "location",
      block: "location",
      gramPanchayat: "location",
      sector: "sector",
      subSector: "sector",
      financialYear: "financial",
      schemeStatus: "status",
      tenderStatus: "status",
      paymentStatus: "status",
      ddcCumCEOName: "officer",
      districtEngineerName: "officer",
      ddo: "officer",
      searchParams: "search",
    };
    return categoryMap[key] || "search";
  };

  const getFilterDisplayName = (key) => {
    const displayNames = {
      searchParams: "Search",
      district: "District",
      block: "Block",
      gramPanchayat: "Gram Panchayat",
      sector: "Sector",
      subSector: "Sub Sector",
      financialYear: "Financial Year",
      schemeStatus: "Scheme Status",
      tenderStatus: "Tender Status",
      paymentStatus: "Payment Status",
      ddcCumCEOName: "DDC-cum-CEO",
      districtEngineerName: "District Engineer",
      ddo: "DDO",
    };
    return displayNames[key] || key;
  };

  const getAppliedFilters = () => {
    return Object.entries(filters)
      .filter(([key, value]) => value && value !== "")
      .map(([key, value]) => ({
        key,
        value,
        category: getFilterCategory(key),
        displayName: getFilterDisplayName(key),
      }));
  };

  const handleRowClick = (schemeId) => {
    navigate(`/scheme/${schemeId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading schemes...</p>
        </div>
      </div>
    );
  }

  const appliedFilters = getAppliedFilters();

  return (
    <div className="">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Proposals List</h1>
          <p className="text-gray-600 mt-2">
            Manage and monitor development schemes
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-5 shadow border border-blue-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-blue-600 text-lg">📊</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Total Schemes
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {schemeStats.total}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-green-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-green-600 text-lg">✅</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-xl font-bold text-green-600">
                  {schemeStats?.totalCompletedSchemes}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-yellow-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <span className="text-yellow-600 text-lg">⚡</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Draft</p>
                <p className="text-xl font-bold text-yellow-600">
                  {schemeStats?.totalDraftSchemes}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-purple-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <span className="text-purple-600 text-lg">💰</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Total Allocation
                </p>
                <p className="text-xl font-bold text-gray-900">
                  ₹{schemeStats?.totalAllocationAmount?.toLocaleString("en-In")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div
        // ref={filtersRef}
        // className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6"
        >
          {/* <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex-1 w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search schemes by name, location, or sector..."
                  value={filters.searchParams}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400">🔍</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <span className="mr-2">🎛️</span>
                Filters
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 bg-white text-blue-600 rounded-full w-5 h-5 text-xs flex items-center justify-center font-bold">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>

              {getActiveFilterCount() > 0 && (
                <button
                  onClick={clearFilters}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                >
                  <span className="mr-2">🗑️</span>
                  Clear All
                </button>
              )}
            </div>
          </div> */}

          {/* Applied Filters */}
          {/* {appliedFilters.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mt-4 flex flex-wrap gap-2"
            >
              {appliedFilters.map((filter) => (
                <motion.div
                  key={filter.key}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`inline-flex items-center px-3 py-1 rounded-lg border text-sm font-medium ${
                    filterCategoryColors[filter.category].bg
                  } ${filterCategoryColors[filter.category].border} ${
                    filterCategoryColors[filter.category].text
                  }`}
                >
                  <span className="mr-1">{filter.displayName}:</span>
                  <span className="font-semibold">{filter.value}</span>
                  <IconButton
                    onClick={() => removeFilter(filter.key)}
                    size="small"
                    sx={{ marginLeft: "4px" }}
                  >
                    <Close className="text-rose-600" fontSize="small" />
                  </IconButton>
                </motion.div>
              ))}
            </motion.div>
          )} */}

          {/* Advanced Filters */}
          {/* <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    District
                  </label>
                  <select
                    value={filters.district}
                    onChange={(e) =>
                      handleFilterChange("district", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Districts</option>
                    {filterOptions.districts.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Block
                  </label>
                  <select
                    value={filters.block}
                    onChange={(e) =>
                      handleFilterChange("block", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Blocks</option>
                    {filterOptions.blocks.map((block) => (
                      <option key={block} value={block}>
                        {block}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gram Panchayat
                  </label>
                  <select
                    value={filters.gramPanchayat}
                    onChange={(e) =>
                      handleFilterChange("gramPanchayat", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All GPs</option>
                    {filterOptions.gramPanchayats.map((gp) => (
                      <option key={gp} value={gp}>
                        {gp}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sector
                  </label>
                  <select
                    value={filters.sector}
                    onChange={(e) =>
                      handleFilterChange("sector", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    <option value="">All Sectors</option>
                    {filterOptions.sectors.map((sector) => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sub Sector
                  </label>
                  <select
                    value={filters.subSector}
                    onChange={(e) =>
                      handleFilterChange("subSector", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    <option value="">All Sub Sectors</option>
                    {filterOptions.subSectors.map((subSector) => (
                      <option key={subSector} value={subSector}>
                        {subSector}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Financial Year
                  </label>
                  <select
                    value={filters.financialYear}
                    onChange={(e) =>
                      handleFilterChange("financialYear", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                  >
                    <option value="">All FY</option>
                    {filterOptions.financialYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheme Status
                  </label>
                  <select
                    value={filters.schemeStatus}
                    onChange={(e) =>
                      handleFilterChange("schemeStatus", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                  >
                    <option value="">All Status</option>
                    {filterOptions.schemeStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DDC-cum-CEO
                  </label>
                  <select
                    value={filters.ddcCumCEOName}
                    onChange={(e) =>
                      handleFilterChange("ddcCumCEOName", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">All DDC-cum-CEO</option>
                    {filterOptions.ddcCumCEOs.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </motion.div>
            )}
          </AnimatePresence> */}
        </div>

        {/* Results Count and Pagination Controls */}
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-gray-600 text-sm">
              Showing{" "}
              <span className="font-semibold text-blue-600">
                {schemes.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-blue-600">
                {pagination.totalCount}
              </span>{" "}
              schemes
              {appliedFilters.length > 0 && (
                <span className="text-gray-500">
                  {" "}
                  • {appliedFilters.length} filter(s) applied
                </span>
              )}
            </p>
            <p className="text-gray-500 text-xs mt-1">
              Page {pagination.pageNo} of {pagination.totalPages}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Page Size Selector */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Show</InputLabel>
              <Select
                value={pagination.pageSize}
                label="Show"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {/* MUI Pagination */}
            {pagination.totalPages > 0 && (
              <Box>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.pageNo}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            )}
          </div>
        </div>

        {/* Schemes Table */}
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          {schemes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3 text-gray-300">🔍</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                No schemes found
              </h3>
              <p className="text-gray-600 text-sm">
                Try adjusting your search criteria or filters
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("schemeName")}
                    >
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        Proposal
                      </th>
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">
                      Scheme
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">
                      Sector
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("allocatedAmount")}
                    >
                      <div className="flex items-center">
                        Amount
                        {sortConfig.key === "allocatedAmount" && (
                          <span className="ml-1 text-blue-600">
                            {sortConfig.direction === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {schemes.map((scheme, index) => (
                    <motion.tr
                      key={scheme._id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => {
                        scheme?.formStatus === "completed"
                          ? navigate("../schemeDetails", {
                              state: { schemeId: scheme?.id },
                            })
                          : navigate("../addNewScheme", {
                              state: {
                                draftSchemeId: scheme?.id,
                                draftSchemeData: scheme,
                              },
                            });
                      }}
                    >
                      <td className="px-4 py-3">
                        {scheme.proposalName || "---"}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-semibold text-gray-900 line-clamp-1">
                            {scheme.schemeName}
                          </div>
                          <div className="text-gray-500 text-xs mt-1">
                            FY: {scheme.financialYear}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {scheme.district}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {scheme.block} • {scheme.gramPanchayat}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {scheme.sector}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {scheme.subSector}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-blue-600">
                          {formatAmount(scheme.allocatedAmount)}
                        </div>
                      </td>
                      <td
                        className="px-4 py-3"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {scheme?.formStatus === "completed" && (
                          <button
                            className="font-bold text-blue-600"
                            onClick={() =>
                              navigate("../schemeDetails", {
                                state: { schemeId: scheme?.id },
                              })
                            }
                          >
                            View
                          </button>
                        )}
                        {scheme?.formStatus === "draft" && (
                          <button
                            className="font-bold text-blue-600"
                            onClick={() =>
                              navigate("../addNewScheme", {
                                state: {
                                  draftSchemeId: scheme?.id,
                                  draftSchemeData: scheme,
                                },
                              })
                            }
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bottom Pagination */}
        {schemes.length > 0 && (
          <div className="mt-6 flex justify-center">
            <Box>
              <Pagination
                count={pagination.totalPages}
                page={pagination.pageNo}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemesList;
